/**
 * Codex Design System — Spacing, Radius, Containers & Icons
 *
 * All values from Figma Foundations §5–6 and §9.2.
 * 4px base grid — all spacing is a multiple of 4.
 */

// ---------------------------------------------------------------------------
// Spacing scale (4px base)
// ---------------------------------------------------------------------------

/** Base unit for MUI's spacing() function: theme.spacing(4) = 16px */
export const spacingBase = 4;

/** Named spacing tokens — use these for explicit pixel references */
export const spacingTokens = {
  0: 0, // no space
  0.5: 2, // hairline gaps
  1: 4, // tight element gaps, inline spacing
  1.5: 6, // dense list item padding
  2: 8, // default inner padding, icon gaps
  2.5: 10, // compact button padding
  3: 12, // compact component padding
  3.5: 14, // small card padding
  4: 16, // standard component padding
  5: 20, // card padding, medium gaps
  6: 24, // section gaps, large padding
  8: 32, // large section spacing
  10: 40, // page section breaks
  12: 48, // major layout spacing
  16: 64, // hero / banner spacing
  18: 72, // custom (4.5rem)
  20: 80, // extra-large section gaps
  24: 96, // page-level vertical rhythm
} as const;

// ---------------------------------------------------------------------------
// Border radius
// ---------------------------------------------------------------------------

/**
 * Border radius tokens (Figma Foundations §6).
 * The "safe & warm" principle drives generous radii on interactive elements.
 *
 * Component reference:
 *   none  — table cells, code blocks
 *   sm    — chips, badges, tags, tooltips
 *   md    — buttons, inputs, cards, dropdowns, menus, toasts
 *   lg    — modals, dialogs, bottom sheets (top corners only)
 *   xl    — feature cards, hero elements, FABs
 *   2xl   — decorative / large promotional cards
 *   full  — avatars, pills, toggles, round icon buttons
 */
export const radii = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 16,
  xl: 24,
  '2xl': 32,
  full: 9999,
} as const;

export type RadiusKey = keyof typeof radii;

// ---------------------------------------------------------------------------
// Container max-widths
// ---------------------------------------------------------------------------

export const containerWidths = {
  xs: 480, // narrow modals, auth forms, small dialogs
  sm: 640, // side panels, narrow content columns
  md: 768, // content columns, medium modals
  lg: 1024, // main content area, wide modals
  xl: 1280, // full-width content, dashboard grids
  '2xl': 1536, // ultra-wide layouts (rare)
} as const;

// ---------------------------------------------------------------------------
// Icon sizes
// ---------------------------------------------------------------------------

/**
 * Icon sizes (Figma Foundations §9.2).
 * All icons are built on a 24px canvas — these are rendering sizes.
 * Touch target minimums are defined separately (WCAG 2.5.5).
 */
export const iconSizes = {
  xs: 12, // inline badges, status indicators   — 24px touch target min
  sm: 16, // compact buttons, table rows         — 32px touch target min
  md: 20, // default: buttons, inputs, nav       — 40px touch target min
  lg: 24, // emphasis: empty states, features    — 48px touch target min
  xl: 32, // illustrations, onboarding           — 48px touch target min
} as const;

export type IconSize = keyof typeof iconSizes;
